/**
 * Users whose queued changes cannot go up until they sign in again.
 *
 * The push refuses to send one person's work under someone else's token (see
 * `replication.pushByAuthor`). When the token kept for them has expired or been
 * rejected, their documents are held back and they are listed here — the app
 * then asks that person to sign in, purely to release their own queue.
 *
 * Holding the rows back stalls that collection's push until it is resolved.
 * That is deliberate: a stalled sync is visible and fixable, a mis-credited
 * record is neither.
 */

export interface SyncBlocker {
    userId: string;
    /** Login name, checked against whoever signs in to release the queue. */
    username: string;
    name: string;
    /** How many documents are waiting on them, across all collections. */
    count: number;
}

const blockers = new Map<string, SyncBlocker>();
const listeners = new Set<() => void>();

function notify(): void {
    for (const listener of listeners) {
        listener();
    }
}

export function subscribeSyncBlockers(listener: () => void): () => void {
    listeners.add(listener);
    return () => void listeners.delete(listener);
}

export function getSyncBlockers(): SyncBlocker[] {
    return [...blockers.values()];
}

/** Record that `count` documents are waiting on this person's sign-in. */
export function blockSync(blocker: SyncBlocker): void {
    const known = blockers.get(blocker.userId);
    if (known && known.count === blocker.count) {
        return;
    }
    blockers.set(blocker.userId, blocker);
    notify();
}

/** They signed in (or their queue drained) — stop asking. */
export function clearSyncBlocker(userId: string): void {
    if (blockers.delete(userId)) {
        notify();
    }
}

/** Raised when a push held documents back; RxDB retries the batch. */
export class SyncBlockedError extends Error {
    constructor(userIds: string[]) {
        super(`Sync blocked, awaiting sign-in: ${userIds.join(', ')}`);
    }
}
